import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import axios from "../../../services/axiosInstance";
import btorageBooster from "../../../assets/img/btorageBooster.png";
import HeaderLangKr from "../../../assets/img/HeaderLangKr.png";
import HeaderLangUs from "../../../assets/img/HeaderLangUs.png";
import HeaderLangJp from "../../../assets/img/HeaderLangJp.png";
import HeaderLangCn from "../../../assets/img/HeaderLangCn.png";
import HeaderLangId from "../../../assets/img/HeaderLangId.png";
import {mergePacking} from "../../../services/storeLink";
import {SHIP_CARRIERS, pickCarrier, shipFeeText, toShipFeeParams, feeStateFor} from "../../../services/storeShipFee";
import "flag-icons/css/flag-icons.css";
import "../../../css/web/StoreLinkPopup.css";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  쇼핑몰 상품연동 안내/선택 팝업 (웹 주문서 공용)
 * ════════════════════════════════════════════════════════════════════════════════
 *  stage 흐름
 *   notice : 해외배송 2차결제 안내 + 언어선택 + [동의]
 *   fee    : 예상 국제배송료 (상점사가 부피/중량을 보냈을 때만 거친다)
 *   ship   : [단독배송] / [합배송] / [연동상품 데이터삭제]
 *   pick   : 진행중인 주문서가 2건 이상일 때 합포장할 주문서 선택
 *
 *  디자인은 이 팝업 전용이다. (css/web/StoreLinkPopup.css — sl- 접두사)
 *  비토리지 공용 디자인토큰/클래스를 쓰지 않으므로 다른 화면에 영향이 없다.
 *
 *  props
 *   linkedProducts  : 연동된 상품 원본 [{name, wid, ver, hei, weight, ...}]
 *   mergeableOrders : [{orderNumber, regDate, recname, shipco, totalPrice, products:[...]}]
 *   onAlone         : 단독배송 선택 (현재 페이지에 자동입력)
 *   onMerge         : 합배송 선택 (선택된 주문번호 전달)
 *   onClearLink     : 연동상품 데이터삭제
 * ════════════════════════════════════════════════════════════════════════════════
 */

/**
 * 이전 단계로 돌아가는 버튼
 *  헤더 문구와 같은 줄에 놓이는, 눈에 확실히 보이는 크기의 아이콘이다.
 *  [배송방법 선택] 과 [합포장할 주문서 선택] 두 단계가 같은 모양을 쓴다.
 */
function BackButton({label, onClick}) {
    return (
        <button type="button" className="sl-back" aria-label={label} onClick={onClick}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
            </svg>
        </button>
    );
}

/** 비토리지가 제공하는 5개 언어. 표기는 국가코드(영문)로 통일한다. */
const LANGS = [
    {code: 'ko', tag: 'KR', flag: HeaderLangKr},
    {code: 'en', tag: 'US', flag: HeaderLangUs},
    {code: 'ja', tag: 'JP', flag: HeaderLangJp},
    {code: 'zh', tag: 'CN', flag: HeaderLangCn},
    {code: 'id', tag: 'ID', flag: HeaderLangId},
];

/** 카드에 한 줄씩 보여줄 상품명 최대 개수. 나머지는 +N 으로 접는다. */
const ITEM_ROWS = 4;

function StoreLinkPopup({theme, linkedProducts = [], mergeableOrders = [], hideAlone, onAlone, onMerge, onClearLink}) {

    const {t, i18n} = useTranslation();
    const [stage, setStage] = useState('notice');
    const [langOpen, setLangOpen] = useState(false);
    const langRef = useRef(null);

    /*
     * 상점사가 보낸 상품들을 한 상자에 담았다고 보고 바깥치수를 추정한다.
     *   mode 'full'   : 가로/세로/높이/실무게 모두 산출
     *   mode 'weight' : 치수는 알 수 없고 실무게만 (선편만 조회 가능)
     *   mode 'none'   : 아무것도 알 수 없음 -> 전부 '정보가 없습니다'
     */
    const packing = useMemo(() => mergePacking(linkedProducts), [linkedProducts]);

    // 마운트 중 배경 스크롤 차단
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    // 언어 드랍다운 바깥을 누르면 닫는다
    useEffect(() => {
        if (!langOpen) return;
        const outside = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
        };
        document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, [langOpen]);

    /*
     * 언어를 바꾸면 이 팝업은 물론 다음 단계인 [배송방법 선택] 문구까지 함께 바뀐다.
     * 모든 문구가 t() 로 그려지므로 i18n 언어만 바꾸면 화면 전체가 즉시 따라온다.
     * 사용자가 고른 언어는 헤더 언어선택과 동일하게 보관해 다음 방문에도 유지한다.
     */
    const langChange = (code) => {
        setLangOpen(false);
        i18n.changeLanguage(code);
        try {
            localStorage.setItem('lang', code);
        } catch (e) { /* 저장 실패는 무시 */
        }
    };

    const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

    /*
     * [동의] 이후 어디로 갈지
     *  상점사가 부피/중량을 조금이라도 보냈으면 예상배송료 단계를 거친다.
     *  아무것도 안 보냈다면 보여줄 것이 없으므로 건너뛴다.
     */
    const showFeeStage = linkedProducts.some((p) =>
        [p?.wid, p?.ver, p?.hei, p?.weight].some((v) => Number(v) > 0));

    const afterNotice = () => {
        if (showFeeStage) setStage('fee');
        else goShip();
    };

    /** 배송방법 선택으로 (진행중인 주문서가 없으면 바로 단독배송) */
    const goShip = () => {
        // 주문서수정 화면(hideAlone)은 고를 주문서가 없어도 팝업을 닫지 않는다.
        //  [연동상품 데이터삭제] 를 누를 기회가 있어야 하기 때문이다.
        if (hideAlone || mergeableOrders.length > 0) setStage('ship');
        else onAlone();
    };

    const mergeHandle = () => {
        if (mergeableOrders.length === 1) onMerge(mergeableOrders[0].orderNumber);
        else setStage('pick');
    };

    /*
     * [배송방법 선택] 에서 이전으로
     *  예상 국제배송료를 거쳐 왔으면 그리로, 건너뛰었으면 안내단계로 돌아간다.
     *  (부피/중량이 없으면 그 단계는 애초에 보여줄 것이 없다)
     */
    const backFromShip = () => setStage(showFeeStage ? 'fee' : 'notice');
    const backLabel = showFeeStage ? t('slFeeTitle') : t('slNoticeTitle');

    /** 언어 선택 드랍다운 (안내단계 헤더 우측) */
    const langSelect = (
        <div className="sl-lang" ref={langRef}>
            <button type="button" className="sl-lang-btn"
                    aria-haspopup="listbox" aria-expanded={langOpen} aria-label="Language"
                    onClick={() => setLangOpen((v) => !v)}>
                <img className="sl-lang-flag" src={current.flag} alt=""/>
                <span>{current.tag}</span>
                <span className="sl-lang-caret">▼</span>
            </button>

            {langOpen &&
                <div className="sl-lang-menu" role="listbox">
                    {LANGS.map((l) => (
                        <button key={l.code} type="button" role="option"
                                aria-selected={l.code === current.code}
                                className={`sl-lang-item ${l.code === current.code ? 'is-on' : ''}`}
                                onClick={() => langChange(l.code)}>
                            <img className="sl-lang-flag" src={l.flag} alt=""/>
                            <span>{l.tag}</span>
                            {l.code === current.code && <span className="sl-check">●</span>}
                        </button>
                    ))}
                </div>
            }
        </div>
    );

    return (
        <div className="sl-scope" data-theme={theme}>
            <div className="sl-veil">
                <div className="sl-card">

                    {/* ══ 1) 해외배송 안내 ══ */}
                    {stage === 'notice' &&
                        <div className="sl-stage">
                            <div className="sl-head">
                                <img className="sl-head-mark" src={btorageBooster} alt="Btorage"/>
                                <p className="sl-head-title">{t('slNoticeTitle')}</p>
                                {langSelect}
                            </div>
                            <div className="sl-body">
                                <div className="sl-note">
                                    <p className="sl-text">{t('slNoticeCont1')}</p>
                                </div>
                                <p className="sl-sub">{t('slNoticeCont2')}</p>
                            </div>
                            <div className="sl-foot">
                                <button type="button" className="sl-primary" onClick={afterNotice}>
                                    {t('slAgreeBt')}
                                </button>
                            </div>
                        </div>
                    }

                    {/* ══ 2) 예상 국제배송료 ══ */}
                    {stage === 'fee' &&
                        <FeeStage t={t} packing={packing} onNext={goShip}
                                  onBack={() => setStage('notice')}/>
                    }

                    {/* ══ 3) 단독배송 / 합배송 / 연동해제 ══ */}
                    {stage === 'ship' &&
                        <div className="sl-stage">
                            <div className="sl-head">
                                {/* 예상 국제배송료로 되돌아간다 (그 단계를 건너뛴 경우에는 안내로) */}
                                <BackButton label={backLabel} onClick={backFromShip}/>
                                <img className="sl-head-mark" src={btorageBooster} alt="Btorage"/>
                                <p className="sl-head-title">{t('slShipTitle')}</p>
                            </div>
                            <div className="sl-body">
                                <p className="sl-text" style={{fontSize: "13px"}}>{t('slShipDesc')}</p>
                            </div>
                            <div className="sl-choices">
                                {/*
                                  주문서수정 화면에서는 [단독배송] 을 숨긴다.
                                  이미 만들어진 주문서에 들어와 있으므로 '단독' 이라는 선택지가 성립하지 않는다.
                                */}
                                {!hideAlone &&
                                    <button type="button" className="sl-choice" onClick={onAlone}>
                                        <p className="sl-choice-title">{t('slAlone')}</p>
                                        <p className="sl-choice-desc">{t('slAloneDesc')}</p>
                                    </button>
                                }
                                <button type="button" className="sl-choice is-accent" onClick={mergeHandle}>
                                    <p className="sl-choice-title">{t('slMerge')}</p>
                                    <p className="sl-choice-desc">{t('slMergeDesc')}</p>
                                </button>
                                {/* 연동을 아예 그만두고 일반 고객처럼 주문하고 싶을 때 */}
                                <button type="button" className="sl-choice is-danger" onClick={onClearLink}>
                                    <p className="sl-choice-title">{t('slClear')}</p>
                                    <p className="sl-choice-desc">{t('slClearDesc')}</p>
                                </button>
                            </div>
                        </div>
                    }

                    {/* ══ 4) 합포장할 주문서 선택 ══ */}
                    {stage === 'pick' &&
                        <div className="sl-stage">
                            <div className="sl-head">
                                <BackButton label={t('slShipTitle')} onClick={() => setStage('ship')}/>
                                <p className="sl-head-title">{t('slPickTitle')}</p>
                            </div>
                            <div className="sl-body">
                                <p className="sl-text" style={{fontSize: "13px"}}>{t('slPickDesc')}</p>
                            </div>
                            <div className="sl-list">
                                {mergeableOrders.map((o) => (
                                    <OrderCard key={o.orderNumber} order={o} t={t}
                                               onClick={() => onMerge(o.orderNumber)}/>
                                ))}
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

/**
 * 예상 국제배송료 단계
 *
 *  요금/제한조건은 전부 백엔드(/shipinfo/checkShippingFee)가 판단한다.
 *  배송료비교 화면과 같은 API 를 같은 파라미터로 호출하므로
 *  선편 20kg·부피제한, K-Packet 2kg, FedEx/UPS 30kg 초과 등의 문구가 그대로 나온다.
 */
function FeeStage({t, packing, onNext, onBack}) {

    const [countryList, setCountryList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [fee, setFee] = useState(null);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);

    /*
     * 아무 정보도 없으면 국가를 골라도 의미가 없으므로 선택 UI 자체를 숨긴다.
     *  실무게만 있는 경우에는 선편 요금을 낼 수 있으므로 선택할 수 있어야 한다.
     */
    const canSelectCountry = packing.mode !== 'none';

    useEffect(() => {
        if (!canSelectCountry) return;
        let alive = true;
        axios.get('/order/selectCountry')
            .then((res) => {
                if (!alive) return;
                const list = (res.data || [])
                    .map((c) => ({
                        value: c.countryCode,
                        label: `${c.countryNm} (${c.countryEngNm})`,
                        engName: c.countryEngNm,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                setCountryList(list);
            })
            .catch(() => { /* 조회 실패 시 선택 불가 — 안내문구만 남는다 */ });
        return () => {
            alive = false;
        };
    }, [canSelectCountry]);

    // 드랍다운 바깥 클릭
    useEffect(() => {
        if (!open) return;
        const outside = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, [open]);

    /*
     * 부피/중량 + 국가가 모두 갖춰졌을 때만 조회한다. (조건4)
     * 배송료비교 화면의 checkShippingFee() 와 동일한 요청/응답 처리다.
     */
    const lookup = useCallback((countryCode) => {
        if (packing.mode === 'none' || !countryCode) return;
        setLoading(true);
        axios.get('/shipinfo/checkShippingFee', {params: toShipFeeParams(countryCode, packing)})
            .then((res) => {
                const body = res.data;
                setFee(body && body.success && body.data ? body.data : null);
            })
            .catch(() => setFee(null))
            .finally(() => setLoading(false));
    }, [packing]);

    const pickCountry = (country) => {
        setSelected(country);
        setOpen(false);
        setKeyword('');
        lookup(country.value);
    };

    const filtered = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        if (!q) return countryList;
        return countryList.filter((c) =>
            c.label.toLowerCase().includes(q) || String(c.value).toLowerCase().includes(q));
    }, [countryList, keyword]);

    return (
        <div className="sl-stage">
            <div className="sl-head">
                {/* 최초의 해외배송 안내로 되돌아간다 */}
                <BackButton label={t('slNoticeTitle')} onClick={onBack}/>
                <img className="sl-head-mark" src={btorageBooster} alt="Btorage"/>
                <p className="sl-head-title">{t('slFeeTitle')}</p>
            </div>

            <div className="sl-body">
                {/* 상단 안내 — 요금 / 소요기간 */}
                <div className="sl-guide">
                    <div className="sl-guide-row">
                        <span className="sl-guide-tag">{t('slFeeNoteFeeLabel')}</span>
                        <p className="sl-guide-text">{t('slFeeNoteFee')}</p>
                    </div>
                    <div className="sl-guide-row">
                        <span className="sl-guide-tag">{t('slFeeNoteDayLabel')}</span>
                        <p className="sl-guide-text">{t('slFeeNoteDay')}</p>
                    </div>
                </div>

                {/* 국가 선택 — 부피/중량이 있을 때만 */}
                {canSelectCountry &&
                    <div className="sl-country" ref={boxRef}>
                        <label className="sl-country-label">{t('slFeeCountry')}</label>
                        <button type="button"
                                className={`sl-country-btn ${selected ? '' : 'is-empty'}`}
                                aria-haspopup="listbox" aria-expanded={open}
                                onClick={() => setOpen((v) => !v)}>
                            {selected
                                ? <span className={`fi fi-${String(selected.value).toLowerCase()}`}/>
                                : <span className="sl-country-blank">?</span>}
                            <span className="sl-country-name">
                                {selected ? selected.label : t('slFeeCountryPlaceholder')}
                            </span>
                            <span className="sl-country-caret">▼</span>
                        </button>

                        {open &&
                            <div className="sl-country-menu">
                                <input className="sl-country-search" autoFocus
                                       placeholder={t('slFeeSearch')} value={keyword}
                                       onChange={(e) => setKeyword(e.target.value)}/>
                                <div className="sl-country-list" role="listbox">
                                    {filtered.length === 0
                                        ? <p className="sl-country-empty">{t('slFeeSearch')}</p>
                                        : filtered.map((c) => (
                                            <button key={c.value} type="button" role="option"
                                                    aria-selected={selected?.value === c.value}
                                                    className={`sl-country-item ${selected?.value === c.value ? 'is-on' : ''}`}
                                                    onClick={() => pickCountry(c)}>
                                                <span className={`fi fi-${String(c.value).toLowerCase()}`}/>
                                                <span>{c.label}</span>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        }
                    </div>
                }

                {/* 예상 모의 패킹사이즈 */}
                <div className="sl-pack">
                    <p className="sl-pack-title">{t('slPackTitle')}</p>

                    <div className="sl-pack-row">
                        <span className="sl-pack-label">{t('slPackSize')}</span>
                        {packing.mode === 'full' ?
                            <span className="sl-pack-value">
                                {packing.wid} × {packing.ver} × {packing.hei}
                                <span className="sl-pack-unit">cm</span>
                            </span>
                            :
                            /* 치수가 하나라도 빠지면 상자 크기를 알 수 없다 */
                            <span className="sl-pack-value is-empty">{t('slFeeNoInfo')}</span>
                        }
                    </div>

                    <div className="sl-pack-row">
                        <span className="sl-pack-label">{t('slPackWeight')}</span>
                        {packing.mode === 'none' ?
                            <span className="sl-pack-value is-empty">{t('slFeeNoInfo')}</span>
                            :
                            /* 치수가 없어도 실무게는 전달된 값 그대로 보여준다 */
                            <span className="sl-pack-value">
                                {packing.weight}
                                <span className="sl-pack-unit">kg</span>
                            </span>
                        }
                    </div>
                </div>

                {/* 배송사별 기간 / 요금 */}
                <div className="sl-fee-list">
                    {SHIP_CARRIERS.map((c) => {
                        const state = feeStateFor(packing.mode, !!selected, loading, c.key);
                        const item = state === 'ready' ? pickCarrier(fee, c.key) : null;
                        const {text, isPrice} = shipFeeText(state, item?.finalCost, t);
                        return (
                            <div className="sl-fee-row" key={c.key}>
                                <span className="sl-fee-name">{c.name}</span>
                                <span className="sl-fee-day">{c.days} {t('slFeeDayUnit')}</span>
                                {isPrice && item?.applyWeight ?
                                    <span className="sl-fee-weight">
                                        {t('slFeeApplyWeight')} {item.applyWeight}kg
                                    </span>
                                    : null}
                                <span className={`sl-fee-value ${isPrice ? 'is-price' : ''}`}>{text}</span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="sl-foot">
                <button type="button" className="sl-primary" onClick={onNext}>
                    {t('slNextBt')}
                </button>
            </div>
        </div>
    );
}

/** 진행중인 주문서 1건 카드 */
function OrderCard({order, t, onClick}) {
    const products = Array.isArray(order.products) ? order.products : [];
    const shown = products.slice(0, ITEM_ROWS);
    const rest = products.length - shown.length;

    return (
        <button type="button" className="sl-order" onClick={onClick}>

            {/* 주문번호 / 주문일자 (yyyy-MM-dd) */}
            <div className="sl-order-top">
                <span className="sl-order-no">{order.orderNumber}</span>
                <span className="sl-order-date">{order.regDate}</span>
            </div>

            {/* 수취인명(좌) / 배송사(우) — 좌우 같은 너비로 나눈다 */}
            <div className="sl-order-meta">
                <span className="sl-meta-cell">
                    <span className="sl-meta-label">{t('slRecname')}</span>
                    <span className="sl-meta-value">{order.recname || '-'}</span>
                </span>
                <span className="sl-meta-divider"/>
                <span className="sl-meta-cell is-right">
                    <span className="sl-meta-label">{t('slShipco')}</span>
                    <span className="sl-meta-value">{order.shipco || '-'}</span>
                </span>
            </div>

            {/* 상품명 — 최대 4줄, 나머지는 +N */}
            <div className="sl-order-items">
                {shown.length === 0 ?
                    <p className="sl-item">
                        <span className="sl-item-dot"/>
                        <span className="sl-item-name">-</span>
                    </p>
                    :
                    shown.map((p, i) => (
                        <p className="sl-item" key={i}>
                            <span className="sl-item-dot"/>
                            <span className="sl-item-name">
                                {p.name || '-'}{p.option ? ` / ${p.option}` : ''}
                            </span>
                        </p>
                    ))
                }
                {rest > 0 && <p className="sl-item-more">{`+${rest}`}</p>}
            </div>

            {/* 상품금액 합계 (폼양식 6종 전체의 price + mallShipPrice + fee) */}
            <div className="sl-order-total">
                <span className="sl-total-label">{t('slTotal')}</span>
                <span className="sl-total-value">{Number(order.totalPrice || 0).toLocaleString()}</span>
                <span className="sl-total-unit">KRW</span>
            </div>
        </button>
    )
}

export default StoreLinkPopup
